/* Majova Point – MPForms: shared form helpers (validation, inline errors, conditional fields,
   submit with busy state) + the Kontakt form. Vanilla ES2018, no dependencies.
   Load order (both defer): mp-core.js -> mp-forms.js -> mp-order.js. */
(function (root) {
  'use strict';

  // Exactly isEmail_ in apps-script/Code.gs: one plain address, no lists, display names, quotes or brackets.
  var EMAIL_RE = /^[^\s@<>()\[\]\\,;:"]+@[^\s@<>()\[\]\\,;:"]+\.[^\s@<>()\[\]\\,;:"]+$/;
  var DEFAULT_MSG = {
    required: 'Fyll i det här fältet.',
    format: 'Kontrollera uppgiften.',
    invalid: 'Kontrollera de markerade fälten.',
    failed: 'Det gick inte att skicka. Försök igen om en stund.'
  };
  var ICON_ALERT = '<svg class="mp-alert__icon" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false">' +
    '<circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
    '<path d="M10 5.6v5.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
    '<circle cx="10" cy="14.2" r="1.15" fill="currentColor"/></svg>';

  function str(v) {
    return v === undefined || v === null ? '' : String(v).trim();
  }

  /* ------------------------------------------------ pure validators (tested) */

  var validators = {
    required: function (v) { return str(v) !== ''; },
    // "se 5566778899 01" / "SE556677-8899-01" -> "SE556677889901"
    normVat: function (v) { return str(v).replace(/[\s.\-]+/g, '').toUpperCase(); },
    vat: function (v) { return /^SE\d{12}$/.test(validators.normVat(v)); },
    // "12345" -> "123 45"; anything else is only trimmed
    normZip: function (v) {
      var s = str(v);
      var digits = s.replace(/\s+/g, '');
      return /^\d{5}$/.test(digits) ? digits.slice(0, 3) + ' ' + digits.slice(3) : s;
    },
    zip: function (v) { return /^\d{3}\s?\d{2}$/.test(str(v)); },
    // Same rule and length limit as the backend (isEmail_ in Code.gs).
    email: function (v) {
      var s = str(v);
      return s.length <= 254 && EMAIL_RE.test(s);
    },
    phone: function (v) {
      var s = str(v);
      var digits = s.replace(/\D/g, '').length;
      return /^\+?[\d\s().\/-]+$/.test(s) && digits >= 6 && digits <= 15;
    }
  };

  var normalizers = { vat: validators.normVat, zip: validators.normZip };

  function message(el, kind) {
    return el.getAttribute('data-msg-' + kind) || DEFAULT_MSG[kind];
  }

  // Error message for one control, or '' when valid. Needs only {value, required, getAttribute}.
  function check(el) {
    var value = el.value;
    var rule = el.getAttribute('data-mp-validate');
    if (el.required && !validators.required(value)) return message(el, 'required');
    if (rule && validators[rule] && str(value) !== '' && !validators[rule](value)) return message(el, 'format');
    return '';
  }

  /* ------------------------------------------------------------ DOM helpers */

  // Disabled directly or through an ancestor <fieldset disabled> (hidden conditional groups).
  function isDisabled(el) {
    return el.disabled || el.matches(':disabled') || !!el.closest('fieldset[disabled]');
  }

  function isControl(el) {
    return !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !!el.name &&
      el.type !== 'checkbox' && el.type !== 'radio' && !isDisabled(el);
  }

  // Controls that are validated: named, enabled (also via <fieldset disabled>), not the honeypot.
  function fields(form) {
    return Array.prototype.filter.call(form.elements, function (el) {
      return isControl(el) && el.name !== 'hp' && el.type !== 'hidden';
    });
  }

  function normalize(el) {
    var fn = normalizers[el.getAttribute('data-mp-normalize')];
    if (!fn) return;
    var v = fn(el.value);
    if (v !== el.value) el.value = v;
  }

  function describedBy(el, id, on) {
    var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) {
      return x && x !== id;
    });
    if (on) ids.unshift(id);
    if (ids.length) el.setAttribute('aria-describedby', ids.join(' '));
    else el.removeAttribute('aria-describedby');
  }

  function setError(el, text) {
    var box = el.ownerDocument.getElementById(el.id + '-error');
    var slot = box && (box.querySelector('[data-mp-error-text]') || box);
    var wrap = el.closest('[data-mp-field]');
    el.__mpError = text || '';
    if (text) {
      el.setAttribute('aria-invalid', 'true');
      if (box) {
        slot.textContent = text;
        box.hidden = false;
        describedBy(el, box.id, true);
      }
    } else {
      el.removeAttribute('aria-invalid');
      if (box) {
        slot.textContent = '';
        box.hidden = true;
        describedBy(el, box.id, false);
      }
    }
    if (wrap) wrap.classList.toggle('is-invalid', !!text);
  }

  // Normalises, validates and marks every active field. Returns the invalid controls in DOM order.
  function validate(form) {
    var bad = [];
    fields(form).forEach(function (el) {
      normalize(el);
      var m = check(el);
      setError(el, m);
      if (m) bad.push(el);
    });
    return bad;
  }

  // {name: trimmed value} for every enabled named control, honeypot included, checkboxes excluded.
  function collect(form) {
    var data = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (isControl(el)) data[el.name] = str(el.value);
    });
    return data;
  }

  function focusField(el) {
    var wrap = el.closest('[data-mp-field]') || el;
    try { wrap.scrollIntoView({ block: 'center' }); } catch (e) { /* old browsers */ }
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  function alertNode(doc, kind, text) {
    var box = doc.createElement('div');
    box.className = 'mp-alert mp-alert--' + kind;
    box.innerHTML = ICON_ALERT;
    var p = doc.createElement('p');
    p.textContent = text;
    box.appendChild(p);
    return box;
  }

  function setStatus(form, node) {
    var region = form.querySelector('[data-mp-status]');
    if (!region) return;
    region.textContent = '';
    if (node) region.appendChild(node);
  }

  function showFailure(form) {
    var tpl = form.querySelector('template[data-mp-tpl="error"]');
    var node = tpl && tpl.content ? tpl.content.cloneNode(true) : null;
    setStatus(form, node || alertNode(form.ownerDocument, 'error', DEFAULT_MSG.failed));
  }

  // Backend rejections {error:'missing_<name>'} / {error:'bad_email'} become inline errors on the
  // matching fields. Returns false when nothing matched, so the caller shows the generic error.
  function showServerErrors(form, res) {
    var code = res && typeof res === 'object' && typeof res.error === 'string' ? res.error : '';
    var active = fields(form);
    var hits = [];
    var missing = /^missing_([a-z_]+)$/.exec(code);
    if (missing) {
      hits = active.filter(function (el) { return el.name === missing[1]; });
      hits.forEach(function (el) { setError(el, message(el, 'required')); });
    } else if (code === 'bad_email') {
      var emails = active.filter(function (el) {
        return el.getAttribute('data-mp-validate') === 'email' && str(el.value) !== '';
      });
      hits = emails.filter(function (el) { return !validators.email(el.value); });
      if (!hits.length) hits = emails.filter(function (el) { return el.name === 'email'; });
      hits.forEach(function (el) { setError(el, message(el, 'format')); });
    }
    if (!hits.length) return false;
    setStatus(form, alertNode(form.ownerDocument, 'error', form.getAttribute('data-msg-invalid') || DEFAULT_MSG.invalid));
    focusField(hits[0]);
    return true;
  }

  function setBusy(form, on) {
    var btn = form.querySelector('[data-mp-submit]');
    if (!btn) return;
    var label = btn.querySelector('[data-mp-label]') || btn;
    if (on) {
      // Lock the width so the shorter "sending" label does not make the button jump.
      if (btn.offsetWidth) btn.style.minWidth = btn.offsetWidth + 'px';
      if (!btn.hasAttribute('data-label-idle')) btn.setAttribute('data-label-idle', label.textContent);
      label.textContent = btn.getAttribute('data-label-busy') || label.textContent;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-busy');
    } else {
      label.textContent = btn.getAttribute('data-label-idle') || label.textContent;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-busy');
      btn.style.minWidth = '';
    }
  }

  // Checkbox [data-mp-reveal][aria-controls=id] shows/enables the controlled <fieldset>.
  function syncReveal(cb) {
    var target = cb.ownerDocument.getElementById(cb.getAttribute('aria-controls'));
    if (!target) return;
    var on = cb.checked;
    target.hidden = !on;
    target.disabled = !on;
    var option = cb.closest('[data-mp-option]');
    if (option) option.classList.toggle('is-open', on);
    if (!on) {
      Array.prototype.forEach.call(target.querySelectorAll('[aria-invalid="true"]'), function (el) {
        setError(el, '');
      });
    }
  }

  /* ------------------------------------------------------------------ bind */

  /**
   * Wires a <form data-mp-form novalidate>:
   *  - blur: normalise + validate fields that have a value (or already show an error)
   *  - input: clear an error as soon as the field is fixed (no new errors while typing)
   *  - submit: validate all, focus first error, else busy state + opts.onSubmit(data)
   *  - enables the submit button, which is rendered disabled so the form never submits natively
   *    before this script has run
   * opts.onSubmit returns (a promise of) true when it handled success itself. A backend response
   * with a known field error (missing_<name>, bad_email) marks that field; anything else shows the
   * form's error template.
   */
  function bind(form, opts) {
    if (!form || form.__mpBound) return false;
    form.__mpBound = true;
    opts = opts || {};
    var busy = false;
    var win = form.ownerDocument.defaultView;

    Array.prototype.forEach.call(form.querySelectorAll('[data-mp-reveal]'), function (cb) {
      syncReveal(cb); // browsers may restore checkbox state on back/forward navigation
      cb.addEventListener('change', function () { syncReveal(cb); });
    });

    form.addEventListener('focusout', function (e) {
      var el = e.target;
      if (!isControl(el) || el.name === 'hp') return;
      normalize(el);
      if (str(el.value) !== '' || el.getAttribute('aria-invalid') === 'true') setError(el, check(el));
    });

    form.addEventListener('input', function (e) {
      var el = e.target;
      if (!isControl(el) || el.getAttribute('aria-invalid') !== 'true') return;
      var m = check(el);
      var wasRequired = el.__mpError === message(el, 'required');
      if (!m || (wasRequired && str(el.value) !== '')) setError(el, '');
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy) return;
      setStatus(form, null);
      var bad = validate(form);
      if (bad.length) {
        setStatus(form, alertNode(form.ownerDocument, 'error', form.getAttribute('data-msg-invalid') || DEFAULT_MSG.invalid));
        focusField(bad[0]);
        return;
      }
      busy = true;
      setBusy(form, true);
      var finish = function (result) {
        busy = false;
        setBusy(form, false);
        if (result !== true && !showServerErrors(form, result)) showFailure(form);
      };
      var p;
      try {
        p = Promise.resolve(typeof opts.onSubmit === 'function' ? opts.onSubmit(collect(form)) : false);
      } catch (err) {
        p = Promise.resolve(false);
      }
      p.then(finish, function () { finish(false); });
    });

    if (win) {
      win.addEventListener('pageshow', function (e) {
        if (e.persisted && busy) { busy = false; setBusy(form, false); }
      });
    }

    var submitBtn = form.querySelector('[data-mp-submit]');
    if (submitBtn) submitBtn.disabled = false;
    return true;
  }

  /* ----------------------------------------------------- MPCore passthroughs */

  // MPCore.post (-> object|null). If mp-core.js failed to load, post directly to the same endpoint.
  function post(payload, timeoutMs) {
    var core = root.MPCore;
    if (core && typeof core.post === 'function') {
      try {
        return Promise.resolve(core.post(payload, timeoutMs)).catch(function () { return null; });
      } catch (e) {
        return Promise.resolve(null);
      }
    }
    var url = root.MP && root.MP.endpoint;
    if (!url || typeof root.fetch !== 'function') return Promise.resolve(null);
    var ctrl = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, timeoutMs || 15000);
    return root.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (res) { return res.json(); })
      .then(function (obj) { return obj && typeof obj === 'object' ? obj : null; }, function () { return null; })
      .then(function (obj) { clearTimeout(timer); return obj; });
  }

  function track(event, params) {
    try {
      if (root.MPCore && typeof root.MPCore.track === 'function') root.MPCore.track(event, params || {});
    } catch (e) { /* analytics must never break a form */ }
  }

  function isQA() {
    try { return !!(root.MPCore && root.MPCore.isQA && root.MPCore.isQA() === true); } catch (e) { return false; }
  }

  // Request id for idempotent submits ('' without MPCore; the backend then simply stores each submit).
  function requestId() {
    try {
      if (root.MPCore && typeof root.MPCore.uuid === 'function') return String(root.MPCore.uuid());
    } catch (e) { /* ignore */ }
    return '';
  }

  /* ---------------------------------------------------------- Kontakt form */

  function showDone(box, focus) {
    var form = box.querySelector('form[data-mp-form]');
    var done = box.querySelector('[data-mp-done]');
    if (form) form.hidden = true;
    if (!done) return;
    done.hidden = false;
    if (focus) {
      try { done.focus({ preventScroll: true }); } catch (e) { done.focus(); }
      try { done.scrollIntoView({ block: 'center' }); } catch (e) { /* ignore */ }
    }
  }

  function initContact(scope) {
    var host = scope && scope.querySelectorAll ? scope : root.document;
    Array.prototype.forEach.call(host.querySelectorAll('[data-mp-contact]'), function (box) {
      var form = box.querySelector('form[data-mp-form]');
      if (!form) return;
      // One id per page view: a retry after a lost response is not stored or mailed twice.
      var rid = requestId();
      bind(form, {
        onSubmit: function (d) {
          return post({
            action: 'contact',
            name: d.name || '',
            company: d.company || '',
            email: d.email || '',
            message: d.message || '',
            hp: d.hp || '',
            rid: rid,
            qa: isQA()
          }, 25000).then(function (res) {
            if (!res || res.ok !== true) return res;
            track('contact_submit', {});
            showDone(box, true);
            return true;
          });
        }
      });
      if (box.getAttribute('data-preview') === 'sent') showDone(box, false); // theme editor only
    });
  }

  root.MPForms = {
    validators: validators,
    normalizers: normalizers,
    messages: DEFAULT_MSG,
    check: check,
    setError: setError,
    validate: validate,
    collect: collect,
    bind: bind,
    focusField: focusField,
    post: post,
    track: track,
    isQA: isQA,
    requestId: requestId
  };

  var doc = root.document;
  if (doc && typeof doc.addEventListener === 'function') {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initContact(doc); });
    else initContact(doc);
    doc.addEventListener('shopify:section:load', function (e) { initContact(e.target); });
  }
})(typeof window !== 'undefined' ? window : this);
